import type { EntityCalendarEvent } from '@jms/model';

/**
 * Pins a Visit chip to its own local date on the day grid.
 *
 * A Visit is one attendance on ONE local date — its identity is plan + line + localDate (WBS-10921).
 * The browse row's `startDate`/`endDate` are a resolved appointment WINDOW, not that identity, and the
 * two disagree whenever the window's instants land on either side of local midnight. The merge
 * (`visit.browse.merge.ts`) falls back to UTC for a Service Plan carrying no timezone, so a 07:00–16:00
 * window on a Melbourne site resolves to 17:00 → 02:00 next day and FullCalendar — whose default
 * `nextDayThreshold` is 00:00:00 — quite correctly claims a second day cell for it. The chip then spans
 * Mon→Tue beside single-day chips from lines whose plan does carry a timezone.
 *
 * Clamping here rather than trusting the window means the grid states the Visit's identity, which is
 * what the viewer is reading the grid for. The window survives untouched on the record, so the detail
 * panel and the Table still show the real appointment times.
 */
export function pinVisitEventToLocalDay(event: EntityCalendarEvent, localDate: string | undefined): EntityCalendarEvent {

  if (!localDate) return event;

  // All-day with no end: FullCalendar renders exactly the one date, which IS the answer. An explicit
  // exclusive end would be the day after and is the very span being removed.
  const { end: _dropped, ...withoutEnd } = event;
  return { ...withoutEnd, start: localDate, allDay: true };
}
